import React from 'react';
import { Crown, Skull, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { BracketNode, TournamentMatch } from '../interfaces/tournaments';
import { BracketView } from './BracketView';
import { BracketPanCanvas } from './BracketPanCanvas';

interface DoubleBracketViewProps {
  winners: BracketNode[][];
  losers: BracketNode[][];
  finals: BracketNode[];
  onMatchClick?: (match: TournamentMatch) => void;
  editable?: boolean;
  panCanvasClassName?: string;
}

export const DoubleBracketView: React.FC<DoubleBracketViewProps> = ({
  winners,
  losers,
  finals,
  onMatchClick,
  editable = false,
  panCanvasClassName,
}) => {
  return (
    <BracketPanCanvas
      className={cn('animate-in fade-in duration-300', panCanvasClassName)}
      height={720}
    >
      <div className="inline-block w-max space-y-6 px-2 py-4">
        <BracketSection
          title="Winner Bracket"
          subtitle="Los ganadores avanzan hacia la final"
          icon={<Crown className="h-4 w-4" />}
          accent="emerald"
        >
          <BracketView
            rounds={winners}
            onMatchClick={onMatchClick}
            editable={editable}
            format="double_elimination"
            compact
            embedded
          />
        </BracketSection>

        <BracketSection
          title="Loser Bracket"
          subtitle="Segunda oportunidad para los eliminados"
          icon={<Skull className="h-4 w-4" />}
          accent="rose"
        >
          <BracketView
            rounds={losers}
            onMatchClick={onMatchClick}
            editable={editable}
            format="double_elimination"
            compact
            embedded
          />
        </BracketSection>

        {finals && finals.length > 0 && (
          <div className="relative overflow-hidden rounded-xl border border-primary/30 bg-gradient-to-br from-primary/5 via-card to-card p-6">
            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/5 blur-2xl" />
            <div className="relative mb-6 flex flex-col items-center gap-2 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15">
                <Trophy className="h-5 w-5 text-primary" />
              </div>
              <h3 className="text-lg font-bold text-foreground">Gran Final</h3>
              <p className="text-sm text-muted-foreground">
                El campeón del torneo se decide aquí
              </p>
            </div>
            <div className="relative flex justify-center">
              <BracketView
                rounds={[finals]}
                onMatchClick={onMatchClick}
                editable={editable}
                format="single_elimination"
                compact
                embedded
              />
            </div>
          </div>
        )}
      </div>
    </BracketPanCanvas>
  );
};

interface BracketSectionProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  accent: 'emerald' | 'rose';
  children: React.ReactNode;
}

const BracketSection: React.FC<BracketSectionProps> = ({
  title,
  subtitle,
  icon,
  accent,
  children,
}) => {
  const accentStyles = {
    emerald: {
      border: 'border-emerald-500/20',
      iconBg: 'bg-emerald-500/15',
      iconText: 'text-emerald-400',
    },
    rose: {
      border: 'border-rose-500/20',
      iconBg: 'bg-rose-500/15',
      iconText: 'text-rose-400',
    },
  }[accent];

  return (
    <div
      className={cn(
        'rounded-xl border bg-card/30 p-5',
        accentStyles.border
      )}
    >
      <div className="mb-5 flex items-start gap-3">
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
            accentStyles.iconBg,
            accentStyles.iconText
          )}
        >
          {icon}
        </div>
        <div>
          <h3 className="font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
};

export default DoubleBracketView;
